import * as RadixPopover from "@radix-ui/react-popover";
import { Button, SegmentedButtons, Switch, token } from "@may-i/mash-design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { CountingLineEventType, IntervalZoneEventType } from "~/entities/analysis-config/types";
import { ShortcutGuide } from "./ShortcutGuide";

export type ViewMode = "snapshot" | "floorPlan" | "both";
/** '둘 다' 모드에서 두 캔버스를 쌓는 방향 */
export type BothLayout = "vertical" | "horizontal";

interface CanvasToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (value: ViewMode) => void;
  /** '둘 다' 모드 배치 방향. 가로로 긴 모니터에서 좌우로 놓으면 도면이 커진다 (개선 니즈 #4) */
  bothLayout: BothLayout;
  onBothLayoutChange: (value: BothLayout) => void;
  showLineZone: boolean;
  onShowLineZoneChange: (value: boolean) => void;
  onAddLine: (eventType: CountingLineEventType) => void;
  onAddZone: (eventType: IntervalZoneEventType) => void;
  hasSelectedCamera: boolean;
  /** 선택 카메라에 스냅샷이 있는지. 없으면 이벤트소스 추가 차단 (PO QA 2차 q) */
  selectedCameraHasSnapshot: boolean;
  /** F13: 도면 뷰가 활성 중인지. 스냅샷 없어도 도면 위 이벤트소스 추가 허용 */
  showFloorPlan: boolean;
}

const LINE_TYPES: { type: CountingLineEventType; labelKey: string }[] = [
  { type: "default", labelKey: "summary.lineDefault" },
  { type: "traffic", labelKey: "summary.lineTraffic" },
  { type: "visit", labelKey: "summary.lineVisit" },
];

const ZONE_TYPES: { type: IntervalZoneEventType; labelKey: string }[] = [
  { type: "default", labelKey: "summary.zoneDefault" },
  { type: "purchase", labelKey: "summary.zonePurchase" },
];

export function CanvasToolbar({
  viewMode,
  onViewModeChange,
  bothLayout,
  onBothLayoutChange,
  showLineZone,
  onShowLineZoneChange,
  onAddLine,
  onAddZone,
  hasSelectedCamera,
  selectedCameraHasSnapshot,
  showFloorPlan,
}: CanvasToolbarProps) {
  const { t } = useTranslation("analysis-draft");
  const [lineOpen, setLineOpen] = useState(false);
  const [zoneOpen, setZoneOpen] = useState(false);
  // F13: 스냅샷 없어도 도면 뷰가 활성이면 도면 위 이벤트소스 추가 허용
  const canAdd = selectedCameraHasSnapshot || showFloorPlan;
  const addDisabled = !hasSelectedCamera || !canAdd;
  const addDisabledTitle = !hasSelectedCamera
    ? undefined
    : !canAdd
      ? t("toolbar.addDisabledNoSnapshot")
      : undefined;

  return (
    <Container>
      <LeftGroup>
        {addDisabled ? (
          // disabled 일 때는 popover 자체를 마운트하지 않음 — Trigger 가 button 의 disabled 와 무관하게
          // 부모 div 클릭을 popover open 으로 인식하던 버그 방지 (PO QA 2차 q 보강)
          <div title={addDisabledTitle}>
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon="ic_area_line"
              disabled
            >
              {t("toolbar.addLine")}
            </Button>
          </div>
        ) : (
          <RadixPopover.Root open={lineOpen} onOpenChange={setLineOpen}>
            <RadixPopover.Trigger asChild>
              <Button
                variant="text"
                color="primary"
                size="small"
                startIcon="ic_area_line"
              >
                {t("toolbar.addLine")}
              </Button>
            </RadixPopover.Trigger>
            <RadixPopover.Portal>
              <PopoverContent sideOffset={4} align="start">
                {LINE_TYPES.map((item) => (
                  <MenuItem
                    key={item.type}
                    onClick={() => {
                      onAddLine(item.type);
                      setLineOpen(false);
                    }}
                  >
                    {t(item.labelKey)}
                  </MenuItem>
                ))}
              </PopoverContent>
            </RadixPopover.Portal>
          </RadixPopover.Root>
        )}

        {addDisabled ? (
          <div title={addDisabledTitle}>
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon="ic_area_zone"
              disabled
            >
              {t("toolbar.addZone")}
            </Button>
          </div>
        ) : (
          <RadixPopover.Root open={zoneOpen} onOpenChange={setZoneOpen}>
            <RadixPopover.Trigger asChild>
              <Button
                variant="text"
                color="primary"
                size="small"
                startIcon="ic_area_zone"
              >
                {t("toolbar.addZone")}
              </Button>
            </RadixPopover.Trigger>
            <RadixPopover.Portal>
              <PopoverContent sideOffset={4} align="start">
                {ZONE_TYPES.map((item) => (
                  <MenuItem
                    key={item.type}
                    onClick={() => {
                      onAddZone(item.type);
                      setZoneOpen(false);
                    }}
                  >
                    {t(item.labelKey)}
                  </MenuItem>
                ))}
              </PopoverContent>
            </RadixPopover.Portal>
          </RadixPopover.Root>
        )}
      </LeftGroup>
      <RightGroup>
        <SwitchRow>
          <SwitchLabel>{t("toolbar.viewMode")}</SwitchLabel>
          <SegmentedButtons
            size="xSmall"
            variant="outlined"
            color="primary"
            menu={[
              { value: "snapshot", label: t("toolbar.viewSnapshotOnly") },
              { value: "floorPlan", label: t("toolbar.viewFloorPlanOnly") },
              { value: "both", label: t("toolbar.viewBoth") },
            ]}
            value={viewMode}
            onValueChange={(value) => {
              if (value === "snapshot" || value === "floorPlan" || value === "both") {
                onViewModeChange(value);
              }
            }}
          />
        </SwitchRow>
        {/* 배치 전환은 '둘 다' 일 때만 의미가 있어 그때만 노출 */}
        {viewMode === "both" && (
          <SwitchRow>
            <SwitchLabel>{t("toolbar.bothLayout")}</SwitchLabel>
            <SegmentedButtons
              size="xSmall"
              variant="outlined"
              color="primary"
              menu={[
                { value: "vertical", label: t("toolbar.layoutVertical") },
                { value: "horizontal", label: t("toolbar.layoutHorizontal") },
              ]}
              value={bothLayout}
              onValueChange={(value) => {
                if (value === "vertical" || value === "horizontal") onBothLayoutChange(value);
              }}
            />
          </SwitchRow>
        )}
        <SwitchRow>
          <SwitchLabel>{t("toolbar.showLineZone")}</SwitchLabel>
          <Switch
            size="small"
            color="primary"
            checked={showLineZone}
            onCheckedChange={(v) => { if (typeof v === "boolean") onShowLineZoneChange(v); }}
          />
        </SwitchRow>
        {/* F20: 캔버스 그리기 단축키 안내 */}
        <ShortcutGuide />
      </RightGroup>
    </Container>
  );
}

// --- Styles ---

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fafbff;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
  flex-shrink: 0;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SwitchLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
`;

const PopoverContent = styled(RadixPopover.Content)`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  min-width: 140px;
  border-radius: 16px;
  border: 1px solid #e6e8f0;
  background: #fff;
  box-shadow: -20px 20px 40px -4px rgba(105, 111, 140, 0.24),
    0px 0px 2px 0px rgba(105, 111, 140, 0.16);
  outline: none;
  z-index: 50;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 7px 12px;
  border: none;
  background: transparent;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  cursor: pointer;

  &:hover {
    background: ${token("colors.action.hover")};
  }
`;
